/**
 * Notification Bell Component
 * Shows unread notification count with dropdown
 * Auto-refreshes every 30 seconds
 */

import NotificationsIcon from "@mui/icons-material/Notifications";
import CloseIcon from "@mui/icons-material/Close";
import {
  Badge,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useApiRequest from "utils/Apirequest";
import { convertUTCtoDubai } from "utils/Constant";

const NotificationBell = ({ darkMode }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const apiRequest = useApiRequest();
  const navigate = useNavigate();
  const refreshInterval = useRef(null);

  const open = Boolean(anchorEl);

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      setLoading(true);

      const { data } = await apiRequest(
        "get",
        "notifications/in-app/my-notifications?page=1&limit=5&sortField=createdAt&sortOrder=desc&unreadOnly=true",
        null,
        {},
        {},
        false
      );

      if (data?.success) {
        setNotifications(data.data || []);
        setUnreadCount(data.unreadCount || 0);
      }

    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };
  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      // Optimistic remove
      setNotifications(prev =>
        prev.filter(n => n._id !== notificationId)
      );

      setUnreadCount(prev => Math.max(prev - 1, 0));

      await apiRequest(
        "put",
        `notifications/in-app/${notificationId}/read`,
        {},
        {},
        {}
      );

      // IMPORTANT: refill list so it always shows 5
      fetchNotifications();

    } catch (error) {
      console.error("Error marking notification as read:", error);
      fetchNotifications(); // resync
    }
  };
  // Auto-refresh every 30 seconds
  useEffect(() => {
    fetchNotifications();

    refreshInterval.current = setInterval(() => {
      fetchNotifications();
    }, 30000); // 30 seconds

    return () => {
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (notification) => {
    if (!notification.isRead) {
      markAsRead(notification._id);
    }
    setSelectedNotification(notification);
    setDetailsModalOpen(true);
    handleClose();
  };

  const handleCloseDetailsModal = () => {
    setDetailsModalOpen(false);
    setSelectedNotification(null);
  };

  const handleViewAll = () => {
    handleClose();
    navigate("/notifications-list");
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case "critical":
        return "#DC143C";
      case "high":
        return "#FF4500";
      case "medium":
        return "#FFA500";
      case "low":
        return "#FFD700";
      default:
        return "#17a2b8";
    }
  };

  const getSeverityBadgeColor = (severity) => {
    switch (severity) {
      case "critical":
        return "error";
      case "high":
        return "warning";
      case "medium":
        return "info";
      case "low":
        return "default";
      default:
        return "primary";
    }
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        size="small"
        sx={{ mr: 3, mt: -2 }}
        aria-controls={open ? "notification-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
      >
        <Badge
          badgeContent={unreadCount > 99 ? "99+" : unreadCount} color="error"
          overlap="circular"
          invisible={unreadCount === 0}
        >
          <NotificationsIcon sx={{ color: darkMode ? "#fff" : "#344767" }} />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        id="notification-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 3,
          sx: {
            width: 380,
            maxHeight: 500,
            overflowY: "auto",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            mt: 1.5,
            bgcolor: darkMode ? "#1a2035" : "#fff",
            "& .MuiMenuItem-root": {
              px: 2,
              py: 1.5,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MDBox px={2} py={1.5}>
          <MDTypography variant="h6" fontWeight="medium">
            Notifications
          </MDTypography>
          {unreadCount > 0 && (
            <MDTypography variant="caption" color="text">
              {unreadCount} unread notification{unreadCount > 1 ? "s" : ""}
            </MDTypography>
          )}
        </MDBox>
        <Divider />

        {loading ? (
          <MDBox display="flex" justifyContent="center" py={3}>
            <CircularProgress size={24} />
          </MDBox>
        ) : notifications.length === 0 ? (
          <MDBox px={2} py={3} textAlign="center">
            <MDTypography variant="body2" color="text">
              No notifications
            </MDTypography>
          </MDBox>
        ) : (
          notifications.map((notification, index) => (
            <MenuItem
              key={notification._id}
              onClick={() => handleNotificationClick(notification)}
              sx={{
                borderLeft: `4px solid ${getSeverityColor(notification.severity)}`,
                borderRight: !notification.isRead ? `3px solid #1976d2` : 'none',
                bgcolor: !notification.isRead
                  ? darkMode
                    ? "rgba(25, 118, 210, 0.15)"
                    : "rgba(25, 118, 210, 0.08)"
                  : "transparent",
                "&:hover": {
                  bgcolor: darkMode
                    ? "rgba(255, 255, 255, 0.08)"
                    : "rgba(0, 0, 0, 0.04)",
                },
                mb: 1,
                py: 1.5,
                position: 'relative'
              }}
            >


              <MDBox width="100%">
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  mb={0.5}
                >
                  <MDBox display="flex" alignItems="center" gap={0.5} flex={1}>
                    <MDTypography
                      variant="button"
                      fontWeight={!notification.isRead ? "bold" : "regular"}
                      sx={{ flex: 1, pr: 1 }}
                    >
                      {notification.title}
                    </MDTypography>
                  </MDBox>
                  {notification.severity && (
                    <Chip
                      label={notification.severity.toUpperCase()}
                      size="small"
                      color={getSeverityBadgeColor(notification.severity)}
                      sx={{ height: 20, fontSize: "0.65rem", flexShrink: 0 }}
                    />
                  )}
                </MDBox>
                <MDTypography
                  variant="caption"
                  color="text"
                  display="block"
                  sx={{
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word'
                  }}
                >
                  {notification.message}
                </MDTypography>
                <MDTypography
                  variant="caption"
                  color="text"
                  sx={{ fontSize: "0.7rem", opacity: 0.7 }}
                >
                  {convertUTCtoDubai(notification.createdAt)}
                </MDTypography>
              </MDBox>
            </MenuItem>
          ))
        )}

        {notifications.length > 0 && (
          <>
            <Divider />
            <MDBox px={2} py={1.5} textAlign="center">
              <MDButton
                variant="text"
                color="info"
                size="small"
                onClick={handleViewAll}
                fullWidth
              >
                View All Notifications
              </MDButton>
            </MDBox>
          </>
        )}
      </Menu>

      {/* Notification Details Modal */}
      <Dialog
        open={detailsModalOpen}
        onClose={handleCloseDetailsModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            bgcolor: darkMode ? '#1a2035' : '#fff',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: darkMode ? '#202940' : '#f8f9fa',
            borderBottom: `1px solid ${darkMode ? '#344767' : '#e9ecef'}`,
          }}
        >
          <MDTypography variant="h6" fontWeight="medium">
            Notification Details
          </MDTypography>
          <IconButton onClick={handleCloseDetailsModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2, bgcolor: darkMode ? '#1a2035' : '#fff' }}>
          {selectedNotification && (
            <MDBox>
              {/* Title and Severity */}
              <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <MDTypography variant="h6" fontWeight="medium" sx={{ flex: 1, pr: 2 }}>
                  {selectedNotification.title}
                </MDTypography>
                <Chip
                  label={selectedNotification.severity?.toUpperCase()}
                  color={getSeverityBadgeColor(selectedNotification.severity)}
                  size="small"
                />
              </MDBox>

              {/* Message */}
              <MDBox mb={3}>
                <MDTypography variant="body2" color="text">
                  {selectedNotification.message}
                </MDTypography>
              </MDBox>

              {/* Charger Details */}
              {selectedNotification.data && (
                <MDBox
                  sx={{
                    bgcolor: darkMode ? '#202940' : '#f8f9fa',
                    borderRadius: '8px',
                    p: 2,
                    border: `1px solid ${darkMode ? '#344767' : '#e9ecef'}`,
                  }}
                >
                  <MDTypography variant="button" fontWeight="medium" mb={1.5} display="block">
                    Charger Information
                  </MDTypography>

                  {selectedNotification.data.chargerId && (
                    <MDBox display="flex" mb={1}>
                      <MDTypography variant="caption" fontWeight="medium" sx={{ minWidth: '120px' }}>
                        Charger ID:
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        {selectedNotification.data.chargerId}
                      </MDTypography>
                    </MDBox>
                  )}

                  {selectedNotification.data.chargerName && (
                    <MDBox display="flex" mb={1}>
                      <MDTypography variant="caption" fontWeight="medium" sx={{ minWidth: '120px' }}>
                        Charger Name:
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        {selectedNotification.data.chargerName}
                      </MDTypography>
                    </MDBox>
                  )}

                  {selectedNotification.data.locationName && (
                    <MDBox display="flex" mb={1}>
                      <MDTypography variant="caption" fontWeight="medium" sx={{ minWidth: '120px' }}>
                        Location:
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        {selectedNotification.data.locationName}
                      </MDTypography>
                    </MDBox>
                  )}

                  {selectedNotification.data.connectorId && (
                    <MDBox display="flex" mb={1}>
                      <MDTypography variant="caption" fontWeight="medium" sx={{ minWidth: '120px' }}>
                        Connector ID:
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        {selectedNotification.data.connectorId}
                      </MDTypography>
                    </MDBox>
                  )}

                  {selectedNotification.data.faultCode && (
                    <MDBox display="flex" mb={1}>
                      <MDTypography variant="caption" fontWeight="medium" sx={{ minWidth: '120px' }}>
                        Fault Code:
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        {selectedNotification.data.faultCode}
                      </MDTypography>
                    </MDBox>
                  )}
                </MDBox>
              )}

              {/* Timestamp */}
              <MDBox mt={3} pt={2} borderTop={`1px solid ${darkMode ? '#344767' : '#e9ecef'}`}>
                <MDTypography variant="caption" color="text" sx={{ opacity: 0.7 }}>
                  {convertUTCtoDubai(selectedNotification.createdAt)}
                </MDTypography>
              </MDBox>

              {/* Action Button */}
              {selectedNotification.actionUrl && (
                <MDBox mt={2}>
                  <MDButton
                    variant="gradient"
                    color="info"
                    fullWidth
                    onClick={() => {
                      navigate(selectedNotification.actionUrl);
                      handleCloseDetailsModal();
                    }}
                  >
                    View Details
                  </MDButton>
                </MDBox>
              )}
            </MDBox>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NotificationBell;
